import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocationsService } from '../../../../global/services/locations.service';
import { ToastersService } from '../../../../global/services/toasters.service';
import { SimpleModalComponent } from '../../popups/confirmation-modal/simple-modal.component';
import { TemplateTabs } from '../shared-templates.component';
import { ClientLocation } from '../../../../global/types/ClientLocation';
import { InnerLocation } from '../../../../global/types/InnerLocation';
import { AuthService } from '../../../../global/services/auth.service';
import { PreferredLocation } from '../../../../global/interfaces/prefferedLocation';
import { TenantType } from '../../../../global/enums/TenantType';
import { AbstractComponentWithPagination } from '../../../../global/abstractions/AbstractComponentWithPagination';
import { Pagination } from '../../../../global/types/Pagination';
import { PaginationScrollWithButtonComponent } from '../../pagination-scroll-with-button/pagination-scroll-with-button.component';

type LocationsFilter = { search?: string; clientId?: string; countryIso3?: string };

@Component({
  selector: 'app-shipping-locations',
  templateUrl: './shipping-locations.component.html',
  styleUrls: ['../shared-templates.component.less'],
})
export class ShippingLocationsComponent
  extends AbstractComponentWithPagination<ClientLocation | InnerLocation | PreferredLocation>
  implements OnInit, OnChanges
{
  @Input() filter: LocationsFilter = {};
  @Input() activeTab: TemplateTabs;
  @Input() hasPermissionToSeeClients: boolean;
  @Output() locationDetails: EventEmitter<InnerLocation> = new EventEmitter<InnerLocation>();
  @Output() editLocation: EventEmitter<ClientLocation | InnerLocation> = new EventEmitter<
    ClientLocation | InnerLocation
  >();
  @ViewChild('pagination')
  pagination: PaginationScrollWithButtonComponent;

  openDots = false;
  isLoading = false;

  list: (ClientLocation | InnerLocation | PreferredLocation)[];
  locationId: string;

  constructor(
    private locationService: LocationsService,
    private toasterService: ToastersService,
    private modalService: NgbModal,
    private authService: AuthService,
  ) {
    super('list', toasterService);
  }

  async ngOnInit(): Promise<void> {
   this.pagination?.resetPaginationAndLoadFirstPage();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.filter && !changes.filter.firstChange) {
      await this.emptyListAndLoadFirstPage();
    }
  }

  async getLocations(pagination: Pagination): Promise<(ClientLocation | InnerLocation | PreferredLocation)[]> {
    this.isLoading = true;
    const isShipperAndNotSubCon =
      this.authService.getTenant().type === TenantType.shipper && !this.hasPermissionToSeeClients;
    const { perPage, currentPage } = pagination;
    try {
      if (this.authService.isSuperAdminInZeusView() || isShipperAndNotSubCon) {
        return await this.locationService.getShippingLocations({
          ...this.filter,
          allowNullClientId: true,
          perPage,
          currentPage,
        });
      }
      if (this.hasPermissionToSeeClients) {
        if (this.activeTab === 'ClientLocations')
          return await this.locationService.getClientLocations({
            ...this.filter,
            perPage,
            currentPage,
          });
        else
          return await this.locationService.getInnerLocations({
            ...this.filter,
            perPage,
            currentPage,
          });
      }
    } catch (error) {
      this.toasterService.createErrorToast(error.message);
    } finally {
      this.isLoading = false;
    }
  }

  onEditLocation(location: ClientLocation | InnerLocation) {
    this.editLocation.emit(location);
    this.openDots = false;
  }

  onDetails(location: InnerLocation) {
    this.locationDetails.emit(location);
  }

  async deleteLocation(locationId: string) {
    const modal = this.modalService.open(SimpleModalComponent, {
      centered: true,
      backdropClass: 'popup-background',
      windowClass: 'popup-box',
    });
    modal.componentInstance.width = 24;
    modal.componentInstance.hasCancellationButton = true;
    modal.componentInstance.message = `Are you sure you want to delete this location?`;
    if (!(await modal.result)) return;
    this.isLoading = true;
    try {
      await this.locationService.deleteLocations([locationId]);
      this.toasterService.createNewToast({ type: 'Success', title: 'Location successfully deleted!' });
      await this.emptyListAndLoadFirstPage();
    } catch (error) {
      this.toasterService.createErrorToast(error.message);
    }
    this.isLoading = false;
  }

  showPopupDots(locationId: string) {
    this.openDots = !this.openDots;
    this.locationId = locationId;
  }

  fetchNextPage(pagination: Pagination): Promise<(ClientLocation | InnerLocation | PreferredLocation)[]> {
    return this.getLocations(pagination);
  }

  async emptyListAndLoadFirstPage(): Promise<void> {
    this.list = [];
    this.pagination?.resetPaginationAndLoadFirstPage();
  }
}
